import { useState, useEffect } from 'react';
import { updateQuestionGroup, regenerateQuestionGroupAudio } from '@/Data/Topik';
import { QuestionGroupType } from '@/Data/Topik/types';

type EditQuestionGroupDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    group: QuestionGroupType | null;
    examid: string;
    onSave: () => void;
};

function EditQuestionGroupDialog({
    isOpen,
    onClose,
    group,
    examid,
    onSave
}: EditQuestionGroupDialogProps) {
    const [resourceType, setResourceType] = useState<'audio' | 'text' | 'none'>('none');
    const [audioUrl, setAudioUrl] = useState('');
    const [audioScript, setAudioScript] = useState('');
    const [textContent, setTextContent] = useState('');
    const [sharedQuestionText, setSharedQuestionText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [audioRegenerated, setAudioRegenerated] = useState(false);

    useEffect(() => {
        if (isOpen && group) {
            let rType: 'audio' | 'text' | 'none' = 'none';
            let aUrl = '';
            let aScript = '';
            let tContent = '';

            if (group.shared_resource) {
                if (group.shared_resource.type === 'audio') {
                    rType = 'audio';
                    aUrl = group.shared_resource.audio_url || '';
                    if (group.shared_resource.audio_script && Array.isArray(group.shared_resource.audio_script)) {
                        aScript = JSON.stringify(group.shared_resource.audio_script, null, 2);
                    }
                } else if (group.shared_resource.type === 'text') {
                    rType = 'text';
                    tContent = group.shared_resource.text || '';
                }
            }

            setResourceType(rType);
            setAudioUrl(aUrl);
            setAudioScript(aScript);
            setTextContent(tContent);
            setSharedQuestionText(group.shared_question_text || '');
            setError('');
            setAudioRegenerated(false);
        }
    }, [isOpen, group]);

    const handleRegenerateAudio = () => {
        if (!group || !examid) { return; }
        regenerateQuestionGroupAudio({ examid, groupid: group.groupid });
        setAudioRegenerated(true);
    };

    const handleSave = async () => {
        if (!group) { return; }

        setSaving(true);
        try {
            let sharedResource: Parameters<typeof updateQuestionGroup>[0]['shared_resource'] = null;

            if (resourceType === 'audio') {
                sharedResource = {
                    type: 'audio',
                    audio_url: audioUrl,
                    audio_script: audioScript ? JSON.parse(audioScript) : []
                };
            } else if (resourceType === 'text') {
                sharedResource = {
                    type: 'text',
                    text: textContent
                };
            }

            const result = await updateQuestionGroup({
                groupid: group.groupid,
                shared_resource: sharedResource,
                shared_question_text: sharedQuestionText
            });

            if (result) {
                onClose();
                onSave();
            } else {
                setError('그룹 저장에 실패했습니다.');
            }
        } catch (err: any) {
            setError(err.message || '그룹 저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) { return null; }

    return (
        <>
            <div className="ModalOverlay active" onClick={onClose}></div>
            <div className="Modal active" onClick={(e) => e.stopPropagation()}>
                <div className="ModalContent">
                    <div className="ModalHeader">
                        <h2 className="ModalTitle">그룹 수정: {group?.groupid}</h2>
                    </div>
                    <div className="ModalBody">
                        {error && <div className="Error">{error}</div>}

                        <div className="FormGrid">
                            <div className="InputGroup full">
                                <label className="InputLabel">공통 문제 텍스트</label>
                                <textarea
                                    className="Textarea"
                                    value={sharedQuestionText}
                                    onChange={(e) => setSharedQuestionText(e.target.value)}
                                    rows={2}
                                />
                            </div>

                            <div className="InputGroup">
                                <label className="InputLabel">리소스 유형</label>
                                <select
                                    className="Select"
                                    value={resourceType}
                                    onChange={(e) => setResourceType(e.target.value as typeof resourceType)}
                                >
                                    <option value="none">없음</option>
                                    <option value="audio">오디오</option>
                                    <option value="text">텍스트</option>
                                </select>
                            </div>

                            {resourceType === 'audio' && (
                                <>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">오디오 URL</label>
                                        <input
                                            type="text"
                                            className="Input"
                                            value={audioUrl}
                                            onChange={(e) => setAudioUrl(e.target.value)}
                                            placeholder="https://example.com/audio.mp3"
                                        />
                                        {audioUrl && (
                                            <div className="AudioPreview">
                                                <audio controls>
                                                    <source src={audioUrl} type="audio/mpeg" />
                                                </audio>
                                                <button
                                                    className={`RegenerateButton ${audioRegenerated ? 'success' : ''}`}
                                                    onClick={handleRegenerateAudio}
                                                >
                                                    {audioRegenerated ? '요청됨' : '오디오 재생성'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">오디오 스크립트 (JSON)</label>
                                        <textarea
                                            className="Textarea"
                                            value={audioScript}
                                            onChange={(e) => setAudioScript(e.target.value)}
                                            rows={6}
                                            placeholder='[{"role": "speaker1", "text": "안녕하세요"}]'
                                        />
                                    </div>
                                </>
                            )}

                            {resourceType === 'text' && (
                                <div className="InputGroup full">
                                    <label className="InputLabel">텍스트 내용</label>
                                    <textarea
                                        className="Textarea"
                                        value={textContent}
                                        onChange={(e) => setTextContent(e.target.value)}
                                        rows={4}
                                        placeholder="텍스트 리소스 내용을 입력하세요..."
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="ModalFooter">
                        <button className="ModalButton TertiaryButton" onClick={onClose}>취소</button>
                        <button
                            className="ModalButton PrimaryButton"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditQuestionGroupDialog;
